import { memo, useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { getFileNameFromPath, getFileNameWithoutExtension } from '@goldcast/player-config/src/utils/fileUtils';
import { FONT_DEFAULT_VALUE, FONTS_DEFAULT } from '@goldcast/player-config/src/constants/fonts';
import UploadProgressCard from './UploadProgressCard';
import BrandKitThumbnail from './BrandKitThumbnail';
import { ASPECT_RATIO_OPTIONS, AspectRatioValue } from './constants';
import { BrandKitColorPickerContent } from '@/components/atoms/ColorPicker/BrandKitColorPickerContent';
import FilesInput from '@/components/atoms/inputs/FilesInput/FilesInput';
import Icon from '@/components/atoms/Icon';
import Button from '@/components/atoms/Button/Button';
import TooltipV2 from '@/components/TooltipV2';
import useBrandKitComponent from '@/Pages/BrandKit/components/useBrandKitComponent';
import { getFontEntriesFromPlatformBrandKit } from '@/Pages/BrandKit/components/BrandKitFont/fontKitUtils';
import { getBrandKitUrl } from '@/Pages/BrandKit/components/utils';
import { BRAND_KIT_ACCEPT_FORMATS, BRAND_KIT_INITIAL_COLOR } from '@/Pages/BrandKit/constants';
import { PlatformBrandKitColors } from '@/Pages/BrandKit/types';
import usePlatformBrandKits from '@/hooks/usePlatformBrandKits';
import { loadPlatformBrandKits, saveUploadBrandKitFont } from '@/stores/brandKit';

const FONT_TAKEAWAY_TOOLTIP = 'You can select only 1 font for clip curation preference.';
// Gap between an open picker and its trigger (matches the trigger's former `mt-1` spacing).
const POPOVER_GAP_PX = 4;
// Nudge the colour picker right so an open picker clears the Back button sitting beneath it.
const COLOR_POPOVER_RIGHT_SHIFT_PX = 44;

function deriveFontName(fontPath: string): string {
  const base = getFileNameFromPath(fontPath);
  return (base.includes('.') ? getFileNameWithoutExtension(base) : base).replace(/,/g, '') || fontPath;
}

function rgbToHex(color: string): string {
  const match = color.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i);
  if (!match) {
    return color;
  }
  return '#' + [match[1], match[2], match[3]].map(n => parseInt(n, 10).toString(16).padStart(2, '0')).join('');
}

/**
 * Anchor an open picker to its trigger as a fixed-position overlay.
 *
 * The pickers live inside the New Content modal, whose panel and scroll region clip overflow. A
 * fixed panel resolves against the viewport (the same mechanism the modal itself uses via
 * `fixed inset-0`), so it floats on top of the modal content instead of forcing the modal to grow
 * to fit. Returns viewport coordinates for the panel's top-left plus the trigger width (used by the
 * full-width kit/font dropdowns); recomputes on scroll/resize so it tracks the trigger.
 */
function usePopoverPosition(
  anchorRef: RefObject<HTMLElement>,
  isOpen: boolean
): { top: number; left: number; width: number } | null {
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(null);

  // Attaching listeners isn't layout-synchronous, so a passive effect is correct (and SSR-safe);
  // the panel only renders while open, so computing position after paint causes no visible flash.
  useEffect(() => {
    if (!isOpen) {
      // Clear the stale position so the next open of a since-moved trigger can't flash at the old spot.
      setPosition(null);
      return;
    }
    const update = () => {
      const anchor = anchorRef.current;
      if (!anchor) {
        return;
      }
      const rect = anchor.getBoundingClientRect();
      setPosition({ top: rect.bottom + POPOVER_GAP_PX, left: rect.left, width: rect.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, anchorRef]);

  return position;
}

/**
 * Resolve a brand-kit font entry to the value we persist as `upload_preferences.font_location`.
 *
 * Brand-kit fonts arrive either as a real font-file URL (uploaded fonts) or as a bare CSS family
 * name (web-safe/name-only fonts, e.g. "Inter"). The BE audiogram render downloads
 * `font_location` as a URL, so a bare name fails to download and silently falls back to a default.
 * When the entry is name-only, look it up in the bundled `FONTS_DEFAULT` set (match on name,
 * case/whitespace-insensitive) and persist that `.ttf` URL instead. Only a font we don't bundle
 * falls through as the bare name — which the BE still handles via its own default at render.
 */
function resolveUploadFontLocation(entry: { url: string; name: string }): string {
  const normalized = entry.name.trim().toLowerCase();
  const bundled = FONTS_DEFAULT.find(f => f.name.trim().toLowerCase() === normalized);
  return bundled?.url ?? entry.url;
}

/**
 * Screen 2 of the simplified upload flow: "Clip Curation Preferences" (Figma).
 * Keeps the upload card visible, then lets the user pick a Brand Kit from a dropdown (default =
 * the org's is_default kit) or choose "Add Custom" to set colours + a font by hand. Orgs with no
 * brand kits skip the dropdown and see the custom controls directly ("Add Custom Branding
 * Elements").
 *
 * Custom colours: "+ Add Colour" (parallel to the swatches) opens a colour picker; each colour
 * renders as a swatch with a hover-delete (unbounded, mirroring the brand-kit editor). Persisted
 * as upload_preferences.colours (first also mirrored to `color`).
 * Custom font: a basic-font dropdown parallel to "Upload Custom". Only one font is allowed, so an
 * uploaded font disables the dropdown until removed.
 *
 * The kit/font dropdowns and the colour picker open as fixed-position overlays anchored to their
 * trigger, so they float on top of the modal content rather than growing the modal to fit.
 *
 * Aspect ratio (Target platform): multi-select card grid, always rendered last (after the brand-kit
 * dropdown and, when shown, the custom colour/font controls), none preselected; feeds
 * `aspect_ratios` in upload_preferences. Clip Duration remains out of scope here.
 */
const ClipCurationStep = ({
  contentId,
  colors,
  onColorsChange,
  onFontChange,
  aspectRatios,
  onAspectRatiosChange,
  uploadFile,
  uploadFileName,
  uploadPercentage
}: {
  contentId?: string;
  colors: string[];
  onColorsChange: (colors: string[]) => void;
  onFontChange: (fontUrl: string, fontDisplayName: string) => void;
  aspectRatios: AspectRatioValue[];
  onAspectRatiosChange: (aspectRatios: AspectRatioValue[]) => void;
  uploadFile?: File;
  uploadFileName?: string;
  uploadPercentage: number;
}) => {
  const { brandKits, selectedBrandKitId, isLoading } = usePlatformBrandKits();
  const [isCustom, setIsCustom] = useState(false);
  const [selectedKitId, setSelectedKitId] = useState<string>();

  // Colour add-picker popover (opens from "+ Add Colour"; commits the drafted colour on "Add").
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);
  const [draftColor, setDraftColor] = useState(BRAND_KIT_INITIAL_COLOR);

  // Font state: a chosen basic font, and/or an uploaded custom font. Only one font is allowed for
  // clip curation, so an uploaded font disables the basic-font dropdown until removed.
  const [basicFontName, setBasicFontName] = useState(FONT_DEFAULT_VALUE.name);
  const [isFontDropdownOpen, setIsFontDropdownOpen] = useState(false);
  const [uploadedFont, setUploadedFont] = useState<{ name: string; url: string }>();
  const [isUploadingFont, setIsUploadingFont] = useState(false);
  const pendingFontFileName = useRef<string>();

  const colorPickerRef = useRef<HTMLDivElement>(null);
  const brandKitDropdownRef = useRef<HTMLDivElement>(null);
  const fontDropdownRef = useRef<HTMLDivElement>(null);

  const [isBrandKitDropdownOpen, setIsBrandKitDropdownOpen] = useState(false);

  const brandKitPopoverPosition = usePopoverPosition(brandKitDropdownRef, isBrandKitDropdownOpen);
  const colorPopoverPosition = usePopoverPosition(colorPickerRef, isColorPickerOpen);
  const fontPopoverPosition = usePopoverPosition(fontDropdownRef, isFontDropdownOpen);

  const hasNoBrandKits = !isLoading && brandKits.length === 0;

  useEffect(() => {
    loadPlatformBrandKits();
  }, []);

  const toggleAspectRatio = useCallback(
    (ratio: AspectRatioValue) => {
      onAspectRatiosChange(
        aspectRatios.includes(ratio) ? aspectRatios.filter(r => r !== ratio) : [...aspectRatios, ratio]
      );
    },
    [aspectRatios, onAspectRatiosChange]
  );

  const applyBasicFont = useCallback(
    (name: string) => {
      setBasicFontName(name);
      const font = FONTS_DEFAULT.find(f => f.name === name);
      if (font) {
        onFontChange(font.url, font.name);
      }
    },
    [onFontChange]
  );

  const applyKit = useCallback(
    (kitId: string) => {
      setSelectedKitId(kitId);
      setIsCustom(false);
      const kit = brandKits.find(k => k.id === kitId);
      if (!kit) {
        return;
      }
      const primary = (kit.colors as PlatformBrandKitColors | undefined)?.primary ?? [];
      const hexColors = primary.map(rgbToHex);
      const fontEntry = getFontEntriesFromPlatformBrandKit(kit)[0];
      onColorsChange(hexColors);
      if (fontEntry) {
        onFontChange(resolveUploadFontLocation(fontEntry), fontEntry.name);
      }
    },
    [brandKits, onColorsChange, onFontChange]
  );

  const selectCustom = useCallback(() => {
    // Enter a fresh custom state: no pre-selected colours, and the font reset to the default the
    // dropdown shows, so the shown font matches what will be persisted (rather than carrying over
    // the previously-selected kit's colour/font).
    setIsCustom(true);
    setSelectedKitId(undefined);
    setUploadedFont(undefined);
    onColorsChange([]);
    applyBasicFont(FONT_DEFAULT_VALUE.name);
  }, [onColorsChange, applyBasicFont]);

  // Default-select the org's is_default kit once kits load (unless the user already chose something).
  useEffect(() => {
    if (isLoading || isCustom || selectedKitId || brandKits.length === 0) {
      return;
    }
    const defaultKitId = selectedBrandKitId ?? brandKits[0]?.id;
    if (defaultKitId) {
      applyKit(defaultKitId);
    }
  }, [isLoading, isCustom, selectedKitId, brandKits, selectedBrandKitId, applyKit]);

  // Close open popovers on outside click. Capture phase (`true`) is required: Dialog.Panel has
  // `onMouseDown={e => e.stopPropagation()}` which in React 17+ also stops the native event,
  // so a bubble-phase listener on `document` never fires inside the modal. Capture runs first.
  //
  // Invariant: each picker panel renders as a React child of its trigger wrapper (colorPickerRef /
  // brandKitDropdownRef / fontDropdownRef), so `contains()` still matches clicks inside the panel
  // even though `position: fixed` floats it elsewhere on screen. Do not portal or lift a panel out
  // of its wrapper — that would make these checks treat in-panel clicks as outside and close it.
  useEffect(() => {
    if (!isColorPickerOpen && !isBrandKitDropdownOpen && !isFontDropdownOpen) {
      return;
    }
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (isColorPickerOpen && colorPickerRef.current && !colorPickerRef.current.contains(target)) {
        setIsColorPickerOpen(false);
      }
      if (isBrandKitDropdownOpen && brandKitDropdownRef.current && !brandKitDropdownRef.current.contains(target)) {
        setIsBrandKitDropdownOpen(false);
      }
      if (isFontDropdownOpen && fontDropdownRef.current && !fontDropdownRef.current.contains(target)) {
        setIsFontDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [isColorPickerOpen, isBrandKitDropdownOpen, isFontDropdownOpen]);

  const openColorPicker = useCallback(() => {
    setDraftColor(BRAND_KIT_INITIAL_COLOR);
    setIsColorPickerOpen(true);
  }, []);

  const addDraftColor = useCallback(() => {
    onColorsChange([...colors, draftColor]);
    setIsColorPickerOpen(false);
  }, [colors, draftColor, onColorsChange]);

  const removeColor = useCallback(
    (index: number) => {
      onColorsChange(colors.filter((_, i) => i !== index));
    },
    [colors, onColorsChange]
  );

  const handleFontUploadComplete = useCallback(
    (filePaths: string[]): Promise<void> => {
      if (!contentId) {
        return Promise.resolve();
      }
      const fontPath = filePaths[0];
      const url = getBrandKitUrl(fontPath);
      return saveUploadBrandKitFont(fontPath, contentId).then(() => {
        const displayName = pendingFontFileName.current ?? deriveFontName(fontPath);
        setUploadedFont({ name: displayName, url });
        onFontChange(url, displayName);
      });
    },
    [contentId, onFontChange]
  );

  const { onFilesUpload } = useBrandKitComponent({
    key: 'fonts',
    title: 'Fonts',
    onUploadComplete: handleFontUploadComplete
  });

  const handleFontFilesUpload = useCallback(
    (files: File[]) => {
      pendingFontFileName.current = files[0]?.name;
      setIsUploadingFont(true);
      onFilesUpload(files)
        .catch(() => undefined)
        .finally(() => setIsUploadingFont(false));
    },
    [onFilesUpload]
  );

  const removeUploadedFont = useCallback(() => {
    setUploadedFont(undefined);
    // Revert the active font to the basic-font dropdown selection.
    const font = FONTS_DEFAULT.find(f => f.name === basicFontName);
    onFontChange(font?.url ?? '', font?.name ?? '');
  }, [basicFontName, onFontChange]);

  const showCustomControls = hasNoBrandKits || isCustom;
  const isFontDropdownDisabled = Boolean(uploadedFont);

  const selectedKit = selectedKitId ? brandKits.find(k => k.id === selectedKitId) : undefined;

  const brandKitButtonLabel = isLoading
    ? 'Loading brand kits...'
    : selectedKit
      ? selectedKit.name
      : isCustom || hasNoBrandKits
        ? 'Custom'
        : 'Select a brand kit';

  const subtitle = 'Select styling preferences for AI clip curation';

  const fontTrigger = (
    <button
      type="button"
      data-testid="font-dropdown-trigger"
      disabled={isFontDropdownDisabled}
      onClick={() => setIsFontDropdownOpen(prev => !prev)}
      className="flex h-10 w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 hover:bg-slate-50 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400"
    >
      <span className="min-w-0 flex-1 truncate text-left">{basicFontName}</span>
      <Icon name="IconChevronDown" size={16} className="shrink-0 text-slate-500" />
    </button>
  );

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-slate-500">{subtitle}</p>

      <UploadProgressCard file={uploadFile} fileName={uploadFileName} percentage={uploadPercentage} />

      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700">Brand Kit</label>
        <div ref={brandKitDropdownRef} className="relative">
          <button
            type="button"
            data-testid="brand-kit-dropdown-trigger"
            onClick={() => !hasNoBrandKits && !isLoading && setIsBrandKitDropdownOpen(prev => !prev)}
            className={`flex h-14 w-full items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 hover:bg-slate-50 ${isLoading || hasNoBrandKits ? 'pointer-events-none' : ''}`}
          >
            {selectedKit && !isCustom && <BrandKitThumbnail brandKit={selectedKit} />}
            <span className={`min-w-0 flex-1 truncate text-left ${isLoading ? 'opacity-50' : ''}`}>
              {brandKitButtonLabel}
            </span>
            <Icon
              name="IconChevronDown"
              size={16}
              className={`shrink-0 text-slate-500 ${isLoading ? 'opacity-50' : ''}`}
            />
          </button>
          {/*
            Cap at ~4 rows (48px each) so the open list never overlaps the controls below it.
            Gate on the computed position so the panel first paints already placed — otherwise it
            would flash at its collapsed content-width position for one frame before the fixed
            coordinates land.
          */}
          {isBrandKitDropdownOpen && brandKitPopoverPosition && (
            <div
              data-testid="brand-kit-dropdown"
              style={brandKitPopoverPosition}
              className="fixed z-[100] max-h-[200px] overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-md"
            >
              {brandKits.map(kit => (
                <button
                  key={kit.id}
                  type="button"
                  data-testid={`brand-kit-option-${kit.id}`}
                  onClick={() => {
                    applyKit(kit.id);
                    setIsBrandKitDropdownOpen(false);
                  }}
                  className="relative flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-100"
                >
                  <BrandKitThumbnail brandKit={kit} />
                  <span className="min-w-0 flex-1 truncate">{kit.name}</span>
                  {selectedKitId === kit.id && !isCustom && (
                    <span
                      data-testid={`brand-kit-option-selected-${kit.id}`}
                      className="flex size-5 shrink-0 items-center justify-center rounded-full bg-green-500 text-white"
                    >
                      <Icon name="IconCheck" size={12} stroke={2.5} />
                    </span>
                  )}
                </button>
              ))}
              <hr className="-mx-1 my-1 h-px border-0 bg-slate-200" />
              <button
                type="button"
                data-testid="brand-kit-option-custom"
                onClick={() => {
                  selectCustom();
                  setIsBrandKitDropdownOpen(false);
                }}
                className="relative flex w-full items-center gap-3 rounded-md px-2 py-1.5 text-left text-sm text-slate-800 hover:bg-slate-100"
              >
                <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-slate-900 text-white">
                  <Icon name="IconPlus" size={18} />
                </span>
                Add Custom
              </button>
            </div>
          )}
        </div>
      </div>

      {showCustomControls && (
        <>
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Colour</label>
            <div className="flex flex-wrap items-center gap-3">
              {colors.map((color, index) => (
                <div
                  key={`${color}-${index}`}
                  data-testid={`color-swatch-${index}`}
                  className="group relative size-10 shrink-0 overflow-hidden rounded-lg border border-slate-200"
                >
                  <div className="size-full" style={{ backgroundColor: color }} />
                  <button
                    type="button"
                    data-testid={`delete-color-${index}`}
                    onClick={() => removeColor(index)}
                    className="absolute inset-0 hidden items-center justify-center bg-deep-orange-600 text-white group-hover:flex"
                    aria-label={`Remove colour ${color}`}
                  >
                    <Icon name="IconTrash" size={16} />
                  </button>
                </div>
              ))}
              <div ref={colorPickerRef} className="relative">
                <button
                  type="button"
                  data-testid="add-color-button"
                  onClick={openColorPicker}
                  className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50"
                >
                  <Icon name="IconPlus" size={16} className="text-slate-600" />
                  Add Colour
                </button>
                {isColorPickerOpen && colorPopoverPosition && (
                  <div
                    data-testid="add-color-popover"
                    style={{
                      top: colorPopoverPosition.top,
                      left: colorPopoverPosition.left + COLOR_POPOVER_RIGHT_SHIFT_PX
                    }}
                    className="fixed z-[100] w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-lg"
                  >
                    <BrandKitColorPickerContent
                      selectedColor={draftColor}
                      onColorSelect={setDraftColor}
                      hideBrandKitTab
                    />
                    <div className="mt-3 flex justify-end gap-2">
                      <Button
                        variation="outline"
                        trackingId="new-content-color-cancel"
                        onClick={() => setIsColorPickerOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button variation="filled" trackingId="new-content-color-add" onClick={addDraftColor}>
                        Add
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-slate-700">Fonts</label>
            <div className="flex items-center gap-3">
              <div ref={fontDropdownRef} className="relative min-w-0 flex-1">
                {isFontDropdownDisabled ? (
                  <TooltipV2
                    tooltipId="new-content-font-single"
                    tooltipContent={FONT_TAKEAWAY_TOOLTIP}
                    containerClassName="w-full"
                    tooltipElement={fontTrigger}
                  />
                ) : (
                  fontTrigger
                )}
                {/*
                  Cap at ~5 rows (36px each) so the open list never overlaps the controls below it.
                  Gate on the computed position so the panel first paints already placed (no flash).
                */}
                {isFontDropdownOpen && !isFontDropdownDisabled && fontPopoverPosition && (
                  <div
                    data-testid="font-dropdown"
                    style={fontPopoverPosition}
                    className="fixed z-[100] max-h-[188px] overflow-y-auto rounded-lg border border-slate-200 bg-white p-1 shadow-md"
                  >
                    {FONTS_DEFAULT.map(font => (
                      <button
                        key={font.name}
                        type="button"
                        data-testid={`font-option-${font.name}`}
                        onClick={() => {
                          applyBasicFont(font.name);
                          setIsFontDropdownOpen(false);
                        }}
                        className="relative flex w-full items-center rounded-md py-2 pl-8 pr-3 text-left text-sm text-slate-800 hover:bg-slate-100"
                      >
                        {basicFontName === font.name && (
                          <Icon name="IconCheck" size={14} className="absolute left-2 text-green-600" />
                        )}
                        <span className="min-w-0 flex-1 truncate">{font.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {uploadedFont ? (
                <div className="min-w-0 flex-1">
                  <div
                    data-testid="uploaded-font-chip"
                    className="flex h-10 items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 text-sm text-slate-900"
                  >
                    <span className="truncate">{uploadedFont.name}</span>
                    <button
                      type="button"
                      data-testid="remove-uploaded-font"
                      onClick={removeUploadedFont}
                      aria-label="Remove uploaded font"
                      className="shrink-0 text-slate-500 hover:text-slate-800"
                    >
                      <Icon name="IconX" size={16} />
                    </button>
                  </div>
                </div>
              ) : (
                <FilesInput
                  variant="button"
                  onFilesUpload={handleFontFilesUpload}
                  accept={BRAND_KIT_ACCEPT_FORMATS.fonts}
                  title="Upload Custom"
                  icon="IconUpload"
                  multiple={false}
                  isDisabled={isUploadingFont}
                  buttonClassNames="!h-10 !w-auto shrink-0 whitespace-nowrap px-4"
                />
              )}
            </div>
          </div>
        </>
      )}

      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700">Target platform</label>
        <div className="flex gap-4">
          {ASPECT_RATIO_OPTIONS.map(option => {
            const isSelected = aspectRatios.includes(option.value);
            return (
              <button
                key={option.value}
                type="button"
                data-testid={`aspect-ratio-option-${option.value}`}
                aria-pressed={isSelected}
                onClick={() => toggleAspectRatio(option.value)}
                className={`relative flex flex-1 flex-col items-center gap-1 rounded-2xl border p-4 text-center transition-colors ${
                  isSelected ? 'border-theme-border bg-orange-50' : 'border-card-border bg-white hover:bg-slate-50'
                }`}
              >
                {isSelected && <Icon name="IconCheck" size={16} className="absolute right-2 top-2 text-theme-border" />}
                <Icon name={option.icon} size={32} stroke={1} className={'text-black'} />
                <div className="flex flex-col">
                  <span className={`text-sm font-medium text-black`}>{option.ratioLabel}</span>
                  <span className={`text-xs text-slate-400`}>{option.subLabel}</span>
                </div>
                <div className="mt-0.5 flex gap-1">
                  {option.platformIcons.map(platformIcon => (
                    <Icon key={platformIcon} name={platformIcon} size={16} className="text-slate-300" />
                  ))}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default memo(ClipCurationStep);
